/**
 * ProfileSection
 *
 * Einheitliches 3-Spalten-Layout (Titel links, Inhalt rechts) für alle
 * Sektionen im Profil-Tab. Wird sowohl in ProfileEditForm (Personal-Daten,
 * Passwort, Adresse, Passkeys) als auch in den Benachrichtigungs- und
 * Lösch-Sektionen verwendet, damit der visuelle Rhythmus durchgehend ist.
 *
 * Variant 'danger' färbt nur den Titel rot ein (für Account-Löschung).
 */

import React from 'react';
import clsx from 'clsx';

export interface ProfileSectionProps {
  title: string;
  description: string;
  variant?: 'default' | 'danger';
  /**
   * 'split' (Default): Titel + Beschreibung links, Inhalt rechts (3-Spalten-Grid).
   *   Passt für Edit-Formulare mit vielen Feldern.
   * 'stacked': Titel + Beschreibung oben, Inhalt darunter über volle Breite.
   *   Passt für Toggle-Listen, bei denen die Sub-Sektion-Titel selbst schon
   *   Spalten brauchen (Icon | Titel | Toggle).
   */
  layout?: 'split' | 'stacked';
  children: React.ReactNode;
}

export const ProfileSection: React.FC<ProfileSectionProps> = ({
  title,
  description,
  variant = 'default',
  layout = 'split',
  children,
}) => {
  const titleEl = (
    <h2
      className={clsx(
        'text-base font-semibold leading-7',
        variant === 'danger'
          ? 'text-red-600 dark:text-red-400'
          : 'text-gray-900 dark:text-white'
      )}
    >
      {title}
    </h2>
  );

  const descriptionEl = (
    <p className="mt-1 text-sm leading-6 text-gray-600 dark:text-gray-400">
      {description}
    </p>
  );

  if (layout === 'stacked') {
    return (
      <div className="py-8 md:py-12 first:pt-0">
        <div className="mb-6">
          {titleEl}
          {descriptionEl}
        </div>
        <div>{children}</div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-6 py-8 md:py-12 first:pt-0">
      <div>
        {titleEl}
        {descriptionEl}
      </div>
      <div className="md:col-span-2">{children}</div>
    </div>
  );
};

export default ProfileSection;
